import clsx from 'clsx';
import { forwardRef } from 'react';

import {
  lineContentCSS,
  lineNumberContainerCSS,
  lineNumberCSS,
  highlightWrapperCSS,
  lineCSS,
} from './CodeHighlight.sty.js';
import {
  HighlightExtended,
  type HighlightLanguage,
} from './HighlightExtended.js';

/**
 * @internal
 */
export interface CodeHighlightProps {
  /**
   * Whether line numbers should be shown.
   * @defaultValue true
   */
  showLineNumbers?: boolean;
  language?: HighlightLanguage;
  code: string;
  /**
   * Whether the content breaks into new lines automatically or not.
   * @defaultValue false
   */
  lineBreaks?: boolean;
  /** @defaultValue false */
  buttonPadding?: boolean;
  maxHeight?: number;
  /** @defaultValue 'default' */
  size?: 'default' | 'condensed';
}

/**
 * Highlights code with prism tokenisation
 * @internal
 */
export const CodeHighlight = /* @__PURE__ */ forwardRef<
  HTMLPreElement,
  CodeHighlightProps
>((props, forwardedRef) => {
  const {
    code,
    language,
    showLineNumbers = true,
    lineBreaks = false,
    maxHeight,
    size = 'default',
  } = props;

  return (
    <pre
      className={highlightWrapperCSS({ size })}
      ref={forwardedRef}
      style={{
        whiteSpace: lineBreaks ? 'pre-wrap' : 'pre',
        maxHeight: maxHeight,
      }}
    >
      <HighlightExtended language={language} code={code}>
        {({ tokens, getLineProps, getTokenProps }) => (
          <>
            {tokens.map((line, i) => {
              const isSingleLine = tokens.length === 1;
              const {
                className: lineClassName,
                key,
                ...remainingLineProps
              } = getLineProps({ line, key: i });
              return (
                <div
                  className={clsx(
                    lineClassName,
                    lineCSS({ singleLine: isSingleLine }),
                  )}
                  key={i}
                  {...remainingLineProps}
                >
                  {showLineNumbers ? (
                    <span
                      className={clsx('line-number', lineNumberContainerCSS)}
                    >
                      <span
                        className={lineNumberCSS({ size })}
                        data-linenumber={i + 1}
                      />
                    </span>
                  ) : null}
                  <span
                    className={lineContentCSS({ size })}
                    style={{
                      verticalAlign: isSingleLine ? 'middle' : undefined,
                    }}
                  >
                    {line.map((token, key) => {
                      const { key: tokenKey, ...remainingTokenProps } =
                        getTokenProps({ token, key });
                      return <span key={key} {...remainingTokenProps} />;
                    })}
                  </span>
                </div>
              );
            })}
          </>
        )}
      </HighlightExtended>
    </pre>
  );
});

(CodeHighlight as typeof CodeHighlight & { displayName: string }).displayName =
  'CodeHighlight';
